import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PriceCharts = ({ market, timeframe }) => {
  const [chartType, setChartType] = useState('line');
  const [selectedCommodity, setSelectedCommodity] = useState('white-sugar');

  const commodities = [
    { id: 'white-sugar', label: 'White Sugar M30', color: '#1e4a72' },
    { id: 'raw-sugar', label: 'Raw Sugar S30', color: '#ff8c00' },
    { id: 'refined-sugar', label: 'Refined Sugar', color: '#10b981' }
  ];

  const generateMockData = () => {
    const dataPoints = timeframe === '1h' ? 12 : timeframe === '24h' ? 24 : timeframe === '7d' ? 7 : 30;
    const basePrice = 3800;
    
    return Array?.from({ length: dataPoints }, (_, i) => {
      const variation = Math.random() * 200 - 100;
      return {
        time: timeframe === '1h' ? `${i * 5}m` : timeframe === '24h' ? `${i}h` : `Day ${i + 1}`,
        'White Sugar M30': Math.round(basePrice + variation),
        'Raw Sugar S30': Math.round(basePrice - 150 + variation),
        'Refined Sugar': Math.round(basePrice + 250 + variation),
        volume: Math.round(Math.random() * 500 + 200)
      };
    });
  };

  const [chartData] = useState(generateMockData());

  const priceStats = [
    {
      label: 'Current Price',
      value: '₹3,850',
      change: '+2.3%',
      trend: 'up',
      icon: 'TrendingUp'
    },
    {
      label: 'Day High',
      value: '₹3,920',
      change: '+4.1%',
      trend: 'up',
      icon: 'ArrowUp'
    },
    {
      label: 'Day Low',
      value: '₹3,780',
      change: '-0.5%',
      trend: 'down',
      icon: 'ArrowDown'
    },
    {
      label: 'Avg Volume',
      value: '1,250 MT',
      change: '+12%',
      trend: 'up',
      icon: 'BarChart3'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Price Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {priceStats?.map((stat, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">{stat?.label}</span>
              <Icon name={stat?.icon} size={16} className="text-primary" />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-foreground">{stat?.value}</div>
              <div className={`flex items-center gap-1 text-sm font-medium ${
                stat?.trend === 'up' ? 'text-green-500' : 'text-red-500'
              }`}>
                <Icon name={stat?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={14} />
                <span>{stat?.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart Controls */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground font-medium">Chart Type:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setChartType('line')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  chartType === 'line' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name="LineChart" size={16} />
                <span>Line</span>
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  chartType === 'bar' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name="BarChart" size={16} />
                <span>Bar</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground font-medium">Commodity:</span>
            <select
              value={selectedCommodity}
              onChange={(e) => setSelectedCommodity(e?.target?.value)}
              className="px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {commodities?.map((commodity) => (
                <option key={commodity?.id} value={commodity?.id}>
                  {commodity?.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Price Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Price Trend Analysis</h3>
          <p className="text-sm text-muted-foreground">
            Historical price data for {commodities?.find(c => c?.id === selectedCommodity)?.label} - {timeframe} view
          </p>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'line' ? (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              {commodities?.map((commodity) => (
                <Line
                  key={commodity?.id}
                  type="monotone"
                  dataKey={commodity?.label}
                  stroke={commodity?.color}
                  strokeWidth={2}
                  dot={{ fill: commodity?.color, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              {commodities?.map((commodity) => (
                <Bar
                  key={commodity?.id}
                  dataKey={commodity?.label}
                  fill={commodity?.color}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Technical Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Activity" size={18} className="text-primary" />
            <h4 className="font-semibold text-foreground">RSI Indicator</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current RSI</span>
              <span className="font-medium text-foreground">68.5</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-orange-500 h-2 rounded-full" style={{ width: '68.5%' }}></div>
            </div>
            <p className="text-xs text-muted-foreground">Approaching overbought territory</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" size={18} className="text-green-500" />
            <h4 className="font-semibold text-foreground">MACD Signal</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Signal</span>
              <span className="font-medium text-green-500">BULLISH</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Strength</span>
              <span className="font-medium text-foreground">Strong</span>
            </div>
            <p className="text-xs text-muted-foreground">Upward momentum detected</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="BarChart3" size={18} className="text-primary" />
            <h4 className="font-semibold text-foreground">Volume Analysis</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Avg Volume</span>
              <span className="font-medium text-foreground">1,250 MT</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Change</span>
              <span className="font-medium text-green-500">+12%</span>
            </div>
            <p className="text-xs text-muted-foreground">Higher than usual activity</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceCharts;